import { HTMLAttributes, MouseEvent, useCallback } from 'react'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Section from '@/components/base/Section'
import Typography, { TypographyProps } from '@/components/base/Typography'
import { useModal } from '@/contexts/modal'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ParagraphBlockProps = HTMLAttributes<HTMLElement> &
  Partial<{
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    title: string
    paragraph: string
    button: WPLink
    use_modal: boolean
  }>

const ParagraphBlock = ({
  title,
  title_variant,
  title_component,
  paragraph,
  button,
  use_modal
}: ParagraphBlockProps) => {
  const { openArticleModal } = useModal()
  const handleButtonClick = useCallback(
    async (event: MouseEvent) => {
      if (!use_modal) return
      event.preventDefault()
      const target = event.currentTarget
      const href = target.getAttribute('data-href') || ''
      const slug = href.replace('/newsroom/news/', '')
      openArticleModal(slug)
    },
    [use_modal, openArticleModal]
  )

  return (
    <Section>
      <Container>
        <Grid>
          <div
            className={cn([
              'col-span-4 md:col-span-4 md:col-start-2 lg:col-span-6 lg:col-start-4',
              'md:group-[.modal]:col-span-6 lg:group-[.modal]:col-span-12',
              'grid gap-y-gutter'
            ])}
          >
            {title ? (
              <Typography
                variant={title_variant}
                component={title_component}
                className="text-heading"
              >
                {title}
              </Typography>
            ) : null}

            {paragraph || button ? (
              <div className="grid gap-y-[3rem] md:gap-y-[6rem]">
                {paragraph ? (
                  <Typography variant="body" paragraph>
                    {paragraph}
                  </Typography>
                ) : null}

                {button ? (
                  <Button
                    variant="primary"
                    asChild
                    className="before:bg-heading"
                    onClick={handleButtonClick}
                  >
                    <Link
                      href={button.url}
                      target={button.target}
                      data-href={button.url}
                    >
                      <span data-label={button.title}>
                        <Typography variant="label" className="text-white">
                          {button.title}
                        </Typography>
                      </span>
                    </Link>
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        </Grid>
      </Container>
    </Section>
  )
}

export default ParagraphBlock
